import { AsyncPipe, CommonModule, DOCUMENT, Location } from '@angular/common';
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { WINDOW } from '@ng-web-apis/common';
import { I18NEXT_SERVICE, I18NextModule, ITranslationService } from 'angular-i18next';
import { QueryRef } from 'apollo-angular';
import {
  BehaviorSubject,
  from,
  fromEvent,
  interval,
  map,
  merge,
  mergeMap,
  NEVER,
  Observable,
  Subject,
  Subscription,
  takeUntil,
  timer,
  Unsubscribable,
} from 'rxjs';
import { BaseComponent } from 'src/app/_shared/components/base/base.component';
import { EllipsisTooltipDirective } from 'src/app/common/ellipsis-tooltip/ellipsis-tooltip.directive';
import { IconComponent } from 'src/app/common/icon/icon.component';
import { LoadingComponent } from 'src/app/_shared/components/loading/loading.component';
import { METADATA_SERVICE, MetadataService } from 'src/app/common/metadata/metadata.service';
import { PostComponent } from 'src/app/_shared/components/post/post.component';
import { ScrollPositionService } from 'src/app/common/scroll/scroll-position.service';
import { SessionStorageService } from 'src/app/common/storage/storage.service';
import { UserNamePipe } from 'src/app/common/user-name/user-name.pipe';
import { UserNameService } from 'src/app/common/user-name/user-name.service';
import { UserService } from 'src/app/common/user/user.service';
import { HashtagSearchGQL } from 'src/app/pages/search-results/search-results.query.generated';
import {
  CommunityPost,
  Hashtag,
  NotificationFeed,
  Place,
  Post,
  SearchComparator,
  SharrySelection,
  SortDirection,
  User,
} from 'src/types/graphql';
import {
  AllCommunityPostsGQL,
  AllCommunityPostsQuery,
  AllCommunityPostsQueryVariables,
  AutocompleteUsersPlacesGQL,
  CommunityFeaturedHashtagsGQL,
  CommunityHashtagsRankingGQL,
} from '../../community.query.generated';

@Component({
  selector: 'sharry-community-all',
  standalone: true,
  imports: [
    AsyncPipe,
    I18NextModule,
    ReactiveFormsModule,
    PostComponent,
    MatInputModule,
    MatAutocompleteModule,
    IconComponent,
    EllipsisTooltipDirective,
    UserNamePipe,
    LoadingComponent,
    MatButtonModule,
    CommonModule,
  ],
  templateUrl: './community-all.component.html',
  styleUrls: ['./community-all.component.scss', './../../community-common.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommunityAllComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy, AfterViewChecked {
  posts$: Observable<CommunityPost[]>;
  autocompleteOptions$: Observable<Partial<User | Hashtag | Place>[]>;
  hashtagsOptions$: Observable<Partial<User | Hashtag | Place>[]>;
  featuredHashtagsOptions$: Observable<Partial<User | Hashtag | Place>[]>;
  search: FormControl<string> = new FormControl('');
  searchType: 'hashtag' | 'autocomplete';
  query: QueryRef<AllCommunityPostsQuery, AllCommunityPostsQueryVariables>;
  lastPostId: string;
  total = 0;
  isLoadingOldPosts = false;
  tempNewPosts: CommunityPost[] = [];
  hashtag: string;
  userId: string;
  title: string;
  isSeek = false;
  hasParams = false;
  hasToScroll = false;
  coordinates: string;
  postSubject: BehaviorSubject<CommunityPost[]>;
  private postSubscriptions: Unsubscribable[] = [];
  isLoadingPosts = false;
  mobileSuggestion = false;
  @ViewChild('autoCompleteInput', { read: MatAutocompleteTrigger }) autoComplete: MatAutocompleteTrigger;
  @ViewChild('autoCompleteInput') elementRef: ElementRef;
  capacitor = Capacitor;

  /**
   * Variable above used to handle the restoration of the scroll to a post clicked byu the user
   */
  @ViewChildren('listItem') listItems!: QueryList<ElementRef>;
  private scrollPositionSubscription: Subscription;
  hasToScrollToRestorePost = true;
  stopScrollingSub$ = new Subject();

  constructor(
    private allCommunityPostsGQL: AllCommunityPostsGQL,
    private route: ActivatedRoute,
    private router: Router,
    private cd: ChangeDetectorRef,
    private hashtagSearchGQL: HashtagSearchGQL,
    private communityFeaturedHashtagsGQL: CommunityFeaturedHashtagsGQL,
    @Inject(METADATA_SERVICE) private metadataService: MetadataService,
    @Inject(I18NEXT_SERVICE) private translateService: ITranslationService,
    private location: Location,
    private renderer: Renderer2,
    private sessionStorageService: SessionStorageService,
    private userService: UserService,
    private userNameService: UserNameService,
    private autocompleteUsersPlacesGQL: AutocompleteUsersPlacesGQL,
    private communityHashtagsRankingGQL: CommunityHashtagsRankingGQL,
    private scrollPositionService: ScrollPositionService,
    private destroyRef: DestroyRef,
    @Inject(WINDOW) private window: Window,
    @Inject(DOCUMENT) private document: Document,
  ) {
    super();
    this.postSubject = new BehaviorSubject([]);
    this.posts$ = this.postSubject.asObservable();
  }

  scrollEvent = () => {
    if (this.autoComplete?.panelOpen) this.autoComplete?.updatePosition();
  };

  ngOnInit(): void {
    this.window.addEventListener('scroll', this.scrollEvent, { once: true, passive: true });
    this.metadataService.setMetadataPage('community', this.location.path());
    let coordinates: any;
    if (this.searchType === 'autocomplete' && this.search?.value?.startsWith('{')) {
      coordinates = JSON.parse(this.search?.value);
    }

    this.scrollPositionSubscription = this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
      if (event instanceof NavigationStart) {
        const slug = event.url.split('/').at(event.url.split('/').length - 1);

        let post: Post | NotificationFeed = (this.postSubject.value as Post[]).find(p => p.slug === slug);
        // non trova il post, potrebbe essere perchè è stata clicckata un interazione (tipo like) che si chiama NotificationFeed
        if (!post) {
          post = (this.postSubject.value as NotificationFeed[]).find(n => n.reference?.slug === slug);
        }

        if (post) {
          this.scrollPositionService.setPostId(post.id);
        }
      }
    });

    this.subscriptions.push(
      this.route.queryParams.subscribe(params => {
        this.userId = params?.userId;
        this.hashtag = params?.hashtag;
        this.searchType = params?.hashtag ? 'hashtag' : 'autocomplete';
        this.isSeek = params?.seek === 'true';
        this.coordinates = params?.coordinates;
        this.title = params?.title;
        this.hasParams = !!this.userId || !!this.hashtag || this.isSeek || !!this.coordinates || !!this.title;
        this.fetchPosts(params?.hashtag, params?.userId, params?.coordinates, params?.title);
      }),
      fromEvent(Capacitor.isNativePlatform() ? this.document.querySelector('.app-wrapper') : this.window, 'scroll')
        .pipe(
          mergeMap(() => {
            const el = this.document.querySelector('#post-' + this.lastPostId);
            if (
              !this.isLoadingOldPosts &&
              !!el &&
              this.isInViewport(el) &&
              this.postSubject.value.length < this.total
            ) {
              this.isLoadingOldPosts = true;
              this.cd.detectChanges();
              return this.allCommunityPostsGQL.fetch(
                {
                  hashtagName: this.searchType === 'hashtag' ? this.search.value?.replace('#', '') || '' : '',
                  userId:
                    this.searchType === 'autocomplete' && !this.search.value?.startsWith('{') ? this.userId || '' : '',
                  shortAddress:
                    this.searchType === 'autocomplete' && this.search.value?.startsWith('{')
                      ? coordinates?.shortAddress
                      : undefined,
                  title: this.title,
                  limit: 10,
                  skip: this.postSubject.value.length,
                  noNotificationFeeds: !!this.hasParams,
                  selections: this.isSeek
                    ? [SharrySelection.SeekPost]
                    : [SharrySelection.SimplePost, SharrySelection.SeekPost],
                },
                { fetchPolicy: 'no-cache' },
              );
            }
            return NEVER;
          }),
        )
        .subscribe({
          next: r => {
            this.postSubject.next([...this.postSubject.value, ...(r.data.allPosts as CommunityPost[])]);
            if (this.userId) {
              this.sessionStorageService.set(
                `COMMUNITY_POST--${this.userId}${this.isSeek ? '--seek' : ''}`,
                JSON.stringify(this.postSubject.value),
              );
            } else if (this.hashtag) {
              this.sessionStorageService.set(
                `COMMUNITY_POST--#${this.hashtag}${this.isSeek ? '--seek' : ''}`,
                JSON.stringify(this.postSubject.value),
              );
            } else if (this.coordinates) {
              this.sessionStorageService.set(
                `COMMUNITY_POST--#${coordinates?.shortAddress}${this.isSeek ? '--seek' : ''}`,
                JSON.stringify(this.postSubject.value),
              );
            } else if (this.title) {
              this.sessionStorageService.set(
                `COMMUNITY_POST--#${this.title}${this.isSeek ? '--seek' : ''}`,
                JSON.stringify(this.postSubject.value),
              );
            } else {
              this.sessionStorageService.set(
                `COMMUNITY_POST${this.isSeek ? '--seek' : ''}`,
                JSON.stringify(this.postSubject.value),
              );
            }
            this.isLoadingOldPosts = false;
            this.lastPostId = this.postSubject.value[this.postSubject.value.length - 1].id;
            this.cd.detectChanges();
          },
        }),
    );
    this.hashtagsOptions$ = this.communityHashtagsRankingGQL.fetch({ limit: 5 }).pipe(map(r => r.data.hashtagSearch));
    this.featuredHashtagsOptions$ = this.communityFeaturedHashtagsGQL.fetch().pipe(map(r => r.data.hashtagSearch));
    this.autocompleteOptions$ = this.search.valueChanges.pipe(
      map(v => {
        if (typeof v === 'string') {
          this.searchType = v?.startsWith('#') ? 'hashtag' : 'autocomplete';
          return {
            value: v?.replace('#', ''),
            type: this.searchType,
          };
        } else {
          const value =
            (v as any).__typename === 'User'
              ? this.userNameService.name(v)
              : (v as any).__typename === 'Hashtag'
                ? (v as any).name
                : (v as any).placeName;
          this.searchType = (v as any).__typename?.toLowerCase();
          return {
            value,
            type: this.searchType,
          };
        }
      }),
      mergeMap(({ value, type }) => {
        if (type === 'hashtag') {
          return this.hashtagSearchGQL.fetch({
            limit: 5,
            filter: { query: { field: 'name', comparator: SearchComparator.ContWordStartsWith, value } },
            communityTags: true,
            sort: { key: 'taggingsCount', direction: SortDirection.Desc },
          });
        } else {
          return this.autocompleteUsersPlacesGQL.fetch({ limit: 5, text: value });
        }
      }),
      map(r => r.data.results as (Place | User | Hashtag)[]),
    );
  }

  scrollDown() {
    if (this.hasParams && this.isPlatformBrowser() && !this.hasToScroll) {
      this.hasToScroll = true;
      const scroll$ = new Subject<boolean>();
      this.subscriptions.push(
        interval(200)
          .pipe(takeUntil(merge(timer(5000), scroll$)))
          .subscribe(() => {
            if (this.hasParams) {
              this.document.querySelector('.community-all').scrollIntoView({ block: 'start', behavior: 'smooth' });
              scroll$.next(true);
              scroll$.complete();
              this.hasToScroll = false;
            }
          }),
      );
    }
  }

  ngAfterViewInit(): void {
    this.scrollDown();
  }

  ngAfterViewChecked(): void {
    const clickedPostId = this.scrollPositionService.getPostId();

    // used to do the scroll to the post only 1 time, cause this lifecycle re-trigger
    if (clickedPostId && this.hasToScrollToRestorePost) {
      this.hasToScrollToRestorePost = false;
    } else {
      return;
    }

    this.listItems.changes.pipe(takeUntil(this.stopScrollingSub$)).subscribe(_ => {
      if (this.listItems.length === this.postSubject.value?.length) {
        // scroll in view to the post
        setTimeout(() => {
          this.document
            .getElementById('post-' + clickedPostId)
            ?.scrollIntoView({ block: 'start', behavior: 'instant' });
        }, 0);

        // reset post saved
        this.scrollPositionService.setPostId(undefined);
        this.stopScrollingSub$.next(true);
      }
    });
  }

  searchDisplayWith(option?: Hashtag): string | undefined {
    return option?.name;
  }

  onSeekDelete() {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        hashtag: this.hashtag,
        userId: this.userId,
        coordinates: this.coordinates,
        seek: undefined,
      },
      state: { trigger: 'popstate' },
      skipLocationChange: false,
      replaceUrl: true,
    });
  }

  onOptionClick(option: MatAutocompleteSelectedEvent, input: HTMLInputElement) {
    let value;
    if (!option) {
      value = this.search.value;
    } else {
      value = option.option.value;
      if (value === '') return;
      this.search.setValue(
        value.__typename === 'User'
          ? this.userNameService.name(value)
          : value.__typename === 'Place'
            ? value.placeName.split(',')[0]
            : value.name,
      );
    }
    this.search.updateValueAndValidity();
    this.router
      .navigate([], {
        relativeTo: this.route,
        queryParams: {
          hashtag: value.__typename === 'Hashtag' ? value.name : undefined,
          userId: value.__typename === 'User' ? value.id : undefined,
          coordinates:
            value.__typename === 'Place' ? JSON.stringify({ shortAddress: value.placeName.split(',')[0] }) : undefined,
          title: !value.__typename ? value : undefined,
        },
        state: { trigger: 'popstate' },
        skipLocationChange: false,
        replaceUrl: true,
      })
      .then(() => input.blur());
  }

  onOptionMobileClick(option: any, input: HTMLInputElement) {
    let value;
    if (!option) {
      value = this.search.value;
    } else {
      value = option;
      if (value === '') return;
      this.search.setValue(
        value.__typename === 'User'
          ? this.userNameService.name(value)
          : value.__typename === 'Place'
            ? value.placeName.split(',')[0]
            : value.name,
      );
    }
    this.search.updateValueAndValidity();
    this.router
      .navigate([], {
        relativeTo: this.route,
        queryParams: {
          hashtag: value.__typename === 'Hashtag' ? value.name : undefined,
          userId: value.__typename === 'User' ? value.id : undefined,
          coordinates:
            value.__typename === 'Place' ? JSON.stringify({ shortAddress: value.placeName.split(',')[0] }) : undefined,
          title: !value.__typename ? value : undefined,
        },
        state: { trigger: 'popstate' },
        skipLocationChange: false,
        replaceUrl: true,
      })
      .then(() => input.blur());
  }

  onReset() {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      state: { trigger: 'popstate' },
      skipLocationChange: false,
      replaceUrl: true,
    });
  }

  onBlock(data: any) {
    this.postSubject.next([...this.postSubject.value.filter(p => p.id !== data.id)]);
    this.scrollDown();
    if (this.userId) {
      this.sessionStorageService.set(
        `COMMUNITY_POST--${this.userId}${this.isSeek ? '--seek' : ''}`,
        JSON.stringify(this.postSubject.value),
      );
    } else if (this.hashtag) {
      this.sessionStorageService.set(
        `COMMUNITY_POST--#${this.hashtag}${this.isSeek ? '--seek' : ''}`,
        JSON.stringify(this.postSubject.value),
      );
    } else if (this.coordinates) {
      const coordinates = JSON.parse(this.coordinates);
      this.sessionStorageService.set(
        `COMMUNITY_POST--#${coordinates?.shortAddress}${this.isSeek ? '--seek' : ''}`,
        JSON.stringify(this.postSubject.value),
      );
    } else if (this.title) {
      this.sessionStorageService.set(
        `COMMUNITY_POST--#${this.title}${this.isSeek ? '--seek' : ''}`,
        JSON.stringify(this.postSubject.value),
      );
    } else {
      this.sessionStorageService.set(
        `COMMUNITY_POST${this.isSeek ? '--seek' : ''}`,
        JSON.stringify(this.postSubject.value),
      );
    }
  }

  onDelete() {
    this.fetchPosts();
  }

  onFloatingClick() {
    this.renderer.addClass(this.document.querySelector('.sharry-medium.rounded.floating'), 'hidden');
    this.postSubject.next([
      ...this.tempNewPosts,
      ...this.postSubject.value.filter(p => !this.tempNewPosts.some(ps => ps.id === p.id)),
    ]);
    if (this.userId) {
      this.sessionStorageService.set(
        `COMMUNITY_POST--${this.userId}${this.isSeek ? '--seek' : ''}`,
        JSON.stringify(this.postSubject.value),
      );
    } else if (this.hashtag) {
      this.sessionStorageService.set(
        `COMMUNITY_POST--#${this.hashtag}${this.isSeek ? '--seek' : ''}`,
        JSON.stringify(this.postSubject.value),
      );
    } else if (this.coordinates) {
      const coordinates = JSON.parse(this.coordinates);
      this.sessionStorageService.set(
        `COMMUNITY_POST--#${coordinates?.shortAddress}${this.isSeek ? '--seek' : ''}`,
        JSON.stringify(this.postSubject.value),
      );
    } else if (this.title) {
      this.sessionStorageService.set(
        `COMMUNITY_POST--#${this.title}${this.isSeek ? '--seek' : ''}`,
        JSON.stringify(this.postSubject.value),
      );
    } else {
      this.sessionStorageService.set(
        `COMMUNITY_POST${this.isSeek ? '--seek' : ''}`,
        JSON.stringify(this.postSubject.value),
      );
    }
    this.document.querySelector('.community-all').scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  ngOnDestroy() {
    this.window.removeEventListener('scroll', this.scrollEvent);
    this.postSubscriptions.forEach(s => s.unsubscribe());
    this.metadataService.clean();
  }

  private isInViewport(element: Element): boolean {
    const rect = element?.getBoundingClientRect();
    return (
      rect?.top >= 0 &&
      rect?.bottom <= (this.window.innerHeight || this.document.documentElement.clientHeight) + (rect.height || 0) + 300
    );
  }

  private fetchPosts(hashtag?: string, userId?: string, coordinates?: string, title?: string) {
    let coor: any;
    if (coordinates) {
      coor = JSON.parse(coordinates);
    }
    this.query?.stopPolling();
    this.isLoadingPosts = true;
    this.postSubscriptions.forEach(s => s.unsubscribe());
    this.postSubscriptions.push(
      from(
        userId
          ? this.sessionStorageService.get(`COMMUNITY_POST--${userId}${this.isSeek ? '--seek' : ''}`)
          : hashtag
            ? this.sessionStorageService.set(
                `COMMUNITY_POST--#${hashtag}${this.isSeek ? '--seek' : ''}`,
                JSON.stringify(this.postSubject.value),
              )
            : coor?.shortAddress
              ? this.sessionStorageService.get(`COMMUNITY_POST--${coor?.shortAddress}${this.isSeek ? '--seek' : ''}`)
              : title
                ? this.sessionStorageService.get(`COMMUNITY_POST--${title}${this.isSeek ? '--seek' : ''}`)
                : this.sessionStorageService.get(`COMMUNITY_POST${this.isSeek ? '--seek' : ''}`),
      )
        .pipe(
          mergeMap(json => {
            this.postSubject.next(json ? JSON.parse(json) : []);
            this.scrollDown();
            this.query = this.allCommunityPostsGQL.watch(
              {
                hashtagName: !userId ? hashtag?.replaceAll('#', '') || '' : '',
                userId: userId || '',
                shortAddress: coor?.shortAddress,
                title: title,
                limit: 10,
                skip: 0,
                noNotificationFeeds: this.hasParams,
                selections: this.isSeek
                  ? [SharrySelection.SeekPost]
                  : [SharrySelection.SimplePost, SharrySelection.SeekPost],
              },
              { fetchPolicy: 'no-cache' },
            );
            if (hashtag) {
              this.search.setValue(hashtag.startsWith('#') ? hashtag : '#' + hashtag);
              this.search.updateValueAndValidity();
            } else if (!userId && !coor?.latitude && !coor?.longitude) {
              this.query.startPolling(150000); // poll ogni 3min 150000
            }
            return this.query.valueChanges;
          }),
        )
        .subscribe({
          next: r => {
            this.isLoadingPosts = false;
            this.total = Number((r.data as any).total);
            this.cd.detectChanges();
            const posts =
              this.userService?.user?.role === 'admin'
                ? [...(r.data.allPosts as CommunityPost[])]
                : [
                    ...(r.data.allPosts.filter(
                      p => p.__typename === 'NotificationFeed' || (p as Post).status === 'published',
                    ) as CommunityPost[]),
                  ];
            if (this.userId) {
              this.search.setValue(
                this.userNameService.name((posts.filter(p => p.__typename === 'Post') as Post[])[0]?.owner),
              );
              this.search.updateValueAndValidity();
            } else if (this.coordinates) {
              this.search.setValue(JSON.parse(this.coordinates).shortAddress);
              this.search.updateValueAndValidity();
            }
            if (this.postSubject.value.length === 0) {
              this.postSubject.next([...this.postSubject.value, ...posts]);
              this.scrollDown();
            } else {
              if (posts.filter(p => !this.postSubject.value.some(ps => ps.id === p.id)).length) {
                this.renderer.removeClass(
                  this.document.querySelector('.sharry-medium.rounded.floating.hidden'),
                  'hidden',
                );
              }
              // this.postSubject.next(this.postSubject.value.map(p => mergeDeep(p, (posts.find(post => post.id === p.id) || {}))));
              this.tempNewPosts = [...posts, ...this.tempNewPosts.filter(p => !posts.some(ps => ps.id === p.id))];
            }
            this.isLoadingOldPosts = false;
            this.lastPostId = this.postSubject.value[this.postSubject.value.length - 1]?.id;
            if (userId) {
              this.sessionStorageService.set(
                `COMMUNITY_POST--${this.userId}${this.isSeek ? '--seek' : ''}`,
                JSON.stringify(this.postSubject.value),
              );
            } else if (hashtag) {
              this.sessionStorageService.set(
                `COMMUNITY_POST--#${hashtag}${this.isSeek ? '--seek' : ''}`,
                JSON.stringify(this.postSubject.value),
              );
            } else if (this.coordinates) {
              this.sessionStorageService.set(
                `COMMUNITY_POST--#${coor?.shortAddress}${this.isSeek ? '--seek' : ''}`,
                JSON.stringify(this.postSubject.value),
              );
            } else if (this.title) {
              this.sessionStorageService.set(
                `COMMUNITY_POST--#${this.title}${this.isSeek ? '--seek' : ''}`,
                JSON.stringify(this.postSubject.value),
              );
            } else {
              this.sessionStorageService.set(
                `COMMUNITY_POST${this.isSeek ? '--seek' : ''}`,
                JSON.stringify(this.postSubject.value),
              );
            }
            this.cd.detectChanges();
          },
        }),
    );
    this.cd.detectChanges();
  }
}
